import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Upload, ScanLine, Trash2, Plus, Loader2, ArrowLeft, UserPlus, CheckCircle2, Building2, Pencil } from "lucide-react";

const TVA = 8.1;

export default function Scan() {
  const navigate = useNavigate();
  const fileRef = useRef();

  const [step, setStep] = useState("client"); // 'client' | 'scan'
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [newClientMode, setNewClientMode] = useState(false);
  const [newClientName, setNewClientName] = useState("");

  const [image, setImage] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState(null); // null until scanned; array once scanned
  const [invoiceDate, setInvoiceDate] = useState("");

  useEffect(() => {
    api.get("/clients", { params: { status: "active" } }).then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const selectedClient = clients.find((c) => c.id === selectedClientId) || null;
  const clientLabel = newClientMode ? (newClientName || "Nouveau client") : selectedClient?.name;

  const goToScan = () => {
    if (newClientMode && !newClientName.trim()) return toast.error("Saisissez le nom du nouveau client");
    if (!newClientMode && !selectedClientId) return toast.error("Choisissez un client");
    setStep("scan");
  };

  const chooseExisting = (c) => {
    setSelectedClientId(c.id);
    setNewClientMode(false);
    setStep("scan");
  };

  // ---- Scan / OCR ----
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
      setItems(null);
      runScan(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const runScan = async (dataURL) => {
    setScanning(true);
    try {
      const { data } = await api.post("/scan-receipt", { image_base64: dataURL });
      setItems(data.items?.length ? data.items : [{ description: "", quantity: 1, unit_price: 0 }]);
      setInvoiceDate(data.date || "");
      toast.success("Ticket reconnu — vérifiez les consommations");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de la reconnaissance");
      setImage(null);
    } finally {
      setScanning(false);
    }
  };

  const updateItem = (idx, field, value) => {
    setItems((arr) => {
      const next = [...arr];
      next[idx] = { ...next[idx], [field]: field === "description" ? value : parseFloat(value) || 0 };
      return next;
    });
  };
  const addItem = () => setItems((arr) => [...(arr || []), { description: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const gross = items ? items.reduce((s, i) => s + i.quantity * i.unit_price, 0) : 0;
  const tvaAmount = gross - gross / (1 + TVA / 100);
  const subtotal = gross - tvaAmount;

  const createInvoice = async () => {
    if (!items || items.every((i) => !i.description.trim())) return toast.error("Ajoutez au moins un article");
    setSaving(true);
    try {
      const { data } = await api.post("/invoices", {
        client_name: newClientMode ? newClientName : selectedClient?.name,
        company: newClientMode ? "" : (selectedClient?.company || ""),
        client_id: newClientMode ? null : selectedClientId,
        client_email: newClientMode ? "" : (selectedClient?.email || ""),
        date: invoiceDate,
        items: items.filter((i) => i.description.trim()),
        tva_rate: TVA,
        receipt_image: image,
      });
      toast.success("Facture créée");
      navigate(`/factures/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-12" data-testid="scan-page">
      <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-[#666] hover:text-[#5E2129] mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      {/* Step indicator */}
      <div className="flex items-center gap-3 mb-6">
        <span className={`flex items-center gap-2 text-sm font-semibold ${step === "client" ? "text-[#5E2129]" : "text-[#666]"}`}>
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step === "client" ? "bg-[#5E2129] text-white" : "bg-[#EBE5D9] text-[#666]"}`}>1</span>
          Client
        </span>
        <span className="h-px w-8 bg-[#EBE5D9]" />
        <span className={`flex items-center gap-2 text-sm font-semibold ${step === "scan" ? "text-[#5E2129]" : "text-[#666]"}`}>
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step === "scan" ? "bg-[#5E2129] text-white" : "bg-[#EBE5D9] text-[#666]"}`}>2</span>
          Ticket
        </span>
      </div>

      {step === "client" ? (
        /* ------------- STEP 1: choose client ------------- */
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl" data-testid="scan-step-client">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5E2129] mb-2">Étape 1</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mb-6">Pour quel client ?</h1>

          {!newClientMode ? (
            <div className="bg-white border border-[#EBE5D9] rounded-xl overflow-hidden">
              <Command>
                <CommandInput placeholder="Rechercher un client Bexio…" data-testid="client-search-input" />
                <CommandList className="max-h-[420px]">
                  <CommandEmpty>Aucun client trouvé.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="__new__ nouveau client en attente" onSelect={() => { setNewClientMode(true); setSelectedClientId(null); }} data-testid="client-option-new">
                      <UserPlus className="w-4 h-4 mr-2 text-[#D97736]" />
                      <span className="text-[#D97736] font-medium">Nouveau client (en attente)</span>
                    </CommandItem>
                    {clients.map((c) => (
                      <CommandItem key={c.id} value={`${c.name} ${c.company}`} onSelect={() => chooseExisting(c)} data-testid={`client-option-${c.id}`}>
                        <div className="flex flex-col">
                          <span className="text-[#1A1A1A]">{c.name}</span>
                          {c.company && c.company !== c.name && <span className="text-xs text-[#666]">{c.company}</span>}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </div>
          ) : (
            <div className="bg-white border border-[#EBE5D9] rounded-xl p-6 space-y-4" data-testid="new-client-panel">
              <div className="flex items-start gap-3 bg-[#D97736]/10 text-[#D97736] rounded-lg px-4 py-3">
                <UserPlus className="w-5 h-5 mt-0.5 shrink-0" />
                <p className="text-sm">Nouveau client : il sera enregistré <strong>« en attente »</strong>. Vous pourrez le créer dans Bexio ensuite (page Clients).</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5E2129] font-semibold">Nom du client</Label>
                <Input data-testid="new-client-name" value={newClientName} onChange={(e) => setNewClientName(e.target.value)} placeholder="Nom / entreprise" className="mt-2 bg-[#FDFBF7] border-[#EBE5D9]" autoFocus />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setNewClientMode(false); setNewClientName(""); }} className="rounded-full border-[#5E2129]/30 text-[#5E2129] hover:bg-[#5E2129]/5">
                  Choisir un client existant
                </Button>
                <Button data-testid="new-client-continue" onClick={goToScan} className="rounded-full bg-[#5E2129] hover:bg-[#4A1A20] text-white flex-1">
                  Continuer vers le ticket
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      ) : (
        /* ------------- STEP 2: scan ticket ------------- */
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="scan-step-ticket">
          {/* selected client chip */}
          <div className="flex items-center justify-between gap-3 bg-white border border-[#EBE5D9] rounded-xl px-5 py-4 mb-6 max-w-2xl" data-testid="selected-client-chip">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-[#5E2129]/8 flex items-center justify-center shrink-0">
                {newClientMode ? <UserPlus className="w-5 h-5 text-[#D97736]" /> : <Building2 className="w-5 h-5 text-[#5E2129]" />}
              </div>
              <div className="min-w-0">
                <p className="text-xs text-[#666] uppercase tracking-wider">Client</p>
                <p className="font-semibold text-[#1A1A1A] truncate">{clientLabel}{newClientMode && " (en attente)"}</p>
              </div>
            </div>
            <button onClick={() => setStep("client")} data-testid="change-client-btn" className="flex items-center gap-1 text-sm text-[#5E2129] font-semibold hover:gap-2 transition-all shrink-0">
              <Pencil className="w-4 h-4" /> Changer
            </button>
          </div>

          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5E2129] mb-2">Étape 2</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mb-6">Scanner le ticket</h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* left: image */}
            <div>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" data-testid="receipt-file-input" />
              {!image ? (
                <button data-testid="upload-receipt-btn" onClick={() => fileRef.current?.click()}
                  className="w-full aspect-[3/4] max-h-[560px] border-2 border-dashed border-[#EBE5D9] rounded-xl bg-white flex flex-col items-center justify-center gap-4 hover:border-[#5E2129]/40 transition-colors duration-200">
                  <div className="w-16 h-16 rounded-2xl bg-[#5E2129]/8 flex items-center justify-center">
                    <Upload className="w-8 h-8 text-[#5E2129]" />
                  </div>
                  <div className="text-center px-4">
                    <p className="font-semibold text-[#1A1A1A]">Prenez ou importez la photo du ticket</p>
                    <p className="text-sm text-[#666] mt-1">Les consommations seront extraites automatiquement</p>
                  </div>
                </button>
              ) : (
                <div className="relative rounded-xl overflow-hidden border border-[#EBE5D9] bg-white">
                  <img src={image} alt="Ticket" className="w-full max-h-[560px] object-contain" />
                  <AnimatePresence>
                    {scanning && (
                      <>
                        <div className="absolute inset-0 bg-[#5E2129]/10" />
                        <motion.div className="scan-line" initial={{ top: "0%" }} animate={{ top: ["0%", "100%", "0%"] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} />
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#5E2129] text-white text-sm px-4 py-2 rounded-full flex items-center gap-2">
                          <ScanLine className="w-4 h-4 animate-pulse" /> Analyse en cours…
                        </div>
                      </>
                    )}
                  </AnimatePresence>
                  {!scanning && (
                    <button onClick={() => { setImage(null); setItems(null); fileRef.current.value = ""; }} data-testid="change-receipt-btn"
                      className="absolute top-3 right-3 bg-white/90 backdrop-blur px-3 py-2 rounded-full text-sm font-medium text-[#5E2129] hover:bg-white transition-colors">
                      Changer
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* right: items */}
            <div>
              {!items ? (
                <div className="h-full min-h-[300px] flex items-center justify-center text-center border border-[#EBE5D9] rounded-xl bg-white p-10">
                  <div>
                    {scanning ? <Loader2 className="w-8 h-8 animate-spin text-[#5E2129] mx-auto mb-4" /> : <ScanLine className="w-8 h-8 text-[#EBE5D9] mx-auto mb-4" />}
                    <p className="text-[#666]">Les consommations reconnues apparaîtront ici.</p>
                  </div>
                </div>
              ) : (
                <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="bg-white border border-[#EBE5D9] rounded-xl p-6 space-y-5" data-testid="scan-result">
                  <div className="flex items-start gap-3 bg-[#2E4F36]/10 text-[#2E4F36] rounded-lg px-4 py-3">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                    <p className="text-sm">Facture pour <strong>{clientLabel}</strong>. Vérifiez les consommations ci-dessous.</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs uppercase tracking-wider text-[#5E2129] font-semibold">Consommations (TTC)</Label>
                      <button onClick={addItem} data-testid="add-item-btn" className="text-sm text-[#5E2129] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
                        <Plus className="w-4 h-4" /> Ajouter
                      </button>
                    </div>
                    <div className="space-y-2">
                      {items.map((it, idx) => (
                        <div key={idx} className="flex gap-2 items-center" data-testid={`item-row-${idx}`}>
                          <Input value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Article" className="flex-1 bg-[#FDFBF7] border-[#EBE5D9]" />
                          <Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="w-16 bg-[#FDFBF7] border-[#EBE5D9]" />
                          <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} className="w-24 bg-[#FDFBF7] border-[#EBE5D9]" />
                          <button onClick={() => removeItem(idx)} className="text-[#666] hover:text-red-600 transition-colors p-1"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5E2129] font-semibold">Date</Label>
                    <Input data-testid="field-date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} placeholder="JJ.MM.AAAA" className="mt-2 bg-[#FDFBF7] border-[#EBE5D9] max-w-[200px]" />
                  </div>

                  <div className="border-t border-[#EBE5D9] pt-4 space-y-1 text-sm">
                    <div className="flex justify-between text-[#666]"><span>Sous-total (HT)</span><span>CHF {subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between text-[#666]"><span>TVA {TVA}% incluse</span><span>CHF {tvaAmount.toFixed(2)}</span></div>
                    <div className="flex justify-between font-serif text-xl text-[#1A1A1A] pt-1"><span>Total (TTC)</span><span>CHF {gross.toFixed(2)}</span></div>
                  </div>

                  <Button data-testid="create-invoice-btn" onClick={createInvoice} disabled={saving} className="w-full h-12 rounded-full bg-[#5E2129] hover:bg-[#4A1A20] text-white font-semibold transition-colors duration-200">
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer la facture"}
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
